import { Injectable } from '@angular/core';
import { ProctoringSeverityRule, ProctoringViolationTypeRule } from '../models/rule.model';
import { BehaviorSubject } from 'rxjs';
import { ViolationState } from '../models/violation.model';

@Injectable({
	providedIn: 'root'
})
export class ViolationStateServiceTsService {

	private MAX_WARNINGS = 5;
	private _cooldownDurations = new Map<string, number>();
	private warningTimeoutId: any;

	private stateSubject = new BehaviorSubject<ViolationState>({
		warningCount: 0,
		maxWarnings: this.MAX_WARNINGS,
		isTerminated: false,
		currentWarning: null,
		terminationReason: null
	});

	state$ = this.stateSubject.asObservable();
	get snapshot() { return this.stateSubject.value; }

	private gracePeriodMap = new Map<string, number>();
	private GRACE_MS = 1500;
	private cooldownMap = new Map<string, number>();
	private COOLDOWN_MS = 8000;


	setMaxWarnings(n: number) {
		this.stateSubject.next({ ...this.stateSubject.value, maxWarnings: n });
	}


	trigger(type: ProctoringViolationTypeRule, severity: ProctoringSeverityRule) {
		const state = this.stateSubject.value;

		if (state.isTerminated) return;
		if (this.isInGrace(type)) return;
		if (this.isInCooldown(type)) return;

		const cooldown = severity === 'WARNING' ? 2000 : 8000;
		this.cooldownMap.set(type, Date.now());
		this._cooldownDurations.set(type, cooldown);


		const shouldCount = severity === 'CRITICAL';
		const newWarningCount = shouldCount
			? state.warningCount + 1
			: state.warningCount;

		this.stateSubject.next({
			...state,
			warningCount: newWarningCount,
			currentWarning: type
		});

		if (this.warningTimeoutId) {
			clearTimeout(this.warningTimeoutId);
		}
		this.warningTimeoutId = setTimeout(() => {
			this.dismissWarning();
		}, 5000);

		if (newWarningCount >= state.maxWarnings && severity === 'CRITICAL') {
			this.terminate('MAX_WARNINGS_EXCEEDED');
		}
	}


	dismissWarning() {
		if (this.warningTimeoutId) {
			clearTimeout(this.warningTimeoutId);
			this.warningTimeoutId = null;
		}
		const current = this.stateSubject.value.currentWarning;
		if (current) {
			this.gracePeriodMap.set(current, Date.now());
		}
		this.stateSubject.next({
			...this.stateSubject.value,
			currentWarning: null
		});
	}


	terminate(reason: string) {
		this.stateSubject.next({
			...this.stateSubject.value,
			isTerminated: true,
			terminationReason: reason,
			currentWarning: null
		});
	}


	private isInGrace(type: string): boolean {
		const t = this.gracePeriodMap.get(type);
		return !!t && (Date.now() - t) < this.GRACE_MS;
	}


	private isInCooldown(type: string): boolean {
		const t = this.cooldownMap.get(type);
		const duration = this._cooldownDurations.get(type) ?? this.COOLDOWN_MS;
		return !!t && (Date.now() - t) < duration;
	}


	reset(): void {
		if (this.warningTimeoutId) {
			clearTimeout(this.warningTimeoutId);
			this.warningTimeoutId = null;
		}
		this.gracePeriodMap.clear();
		this.cooldownMap.clear();
		this._cooldownDurations.clear();

		this.stateSubject.next({
			warningCount: 0,
			maxWarnings: this.MAX_WARNINGS,
			isTerminated: false,
			currentWarning: null,
			terminationReason: null
		});
	}


	clearTermination(): void {
		const current = this.stateSubject.value;
		this.stateSubject.next({
			...current,
			isTerminated: false,
			terminationReason: null,
			currentWarning: null
		});
	}

}